import { useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  IoEyeOffOutline,
  IoEyeOutline,
  IoKeyOutline,
  IoLockClosedOutline,
  IoMailOutline,
  IoPersonOutline,
  IoShieldCheckmarkOutline,
} from 'react-icons/io5';
import { getLoggedInUser } from '../../utils/permissions';

export default function ProfilePage() {
  const user = getLoggedInUser() || {};
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const initials = String(user.userName || 'User')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const togglePassword = (field) => {
    setVisiblePasswords((current) => ({ ...current, [field]: !current[field] }));
  };

  const passwordFieldProps = (field, icon) => ({
    type: visiblePasswords[field] ? 'text' : 'password',
    slotProps: {
      input: {
        startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label={`${visiblePasswords[field] ? 'Hide' : 'Show'} password`} onClick={() => togglePassword(field)} edge="end">
              {visiblePasswords[field] ? <IoEyeOffOutline /> : <IoEyeOutline />}
            </IconButton>
          </InputAdornment>
        ),
      },
    },
  });

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.currentPassword) return setError('Enter your current password.');
    if (form.newPassword.length < 8) return setError('New password must be at least 8 characters.');
    if (form.newPassword !== form.confirmPassword) return setError('New password and confirmation do not match.');
    if (form.currentPassword === form.newPassword) return setError('New password must be different from the current password.');

    setSaving(true);
    try {
      const response = await axios.post(`${baseURL}/change_own_password`, {
        email: user.userEmail,
        current_password: form.currentPassword,
        new_password: form.newPassword,
        confirm_password: form.confirmPassword,
      });
      setSuccess(response.data?.message || 'Password updated successfully.');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to update your password right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 }, background: 'linear-gradient(145deg, #eef3fb 0%, #f8fafc 55%, #edf4ff 100%)' }}>
      <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
        <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 4, border: '1px solid rgba(23,57,113,.1)', boxShadow: '0 20px 55px rgba(30,64,120,.11)' }}>
          <Box sx={{ position: 'relative', px: { xs: 2.5, md: 5 }, py: { xs: 3.5, md: 4.5 }, color: '#fff', background: 'linear-gradient(120deg, #102c5c 0%, #1d4f91 58%, #3779c5 100%)', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.07)', right: -70, top: -130 }} />
            <Box sx={{ position: 'absolute', width: 150, height: 150, borderRadius: '50%', border: '28px solid rgba(255,255,255,.05)', right: 155, bottom: -105 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }} sx={{ position: 'relative' }}>
              <Avatar sx={{ width: 76, height: 76, bgcolor: '#fff', color: '#173971', fontSize: 25, fontWeight: 900, border: '4px solid rgba(255,255,255,.28)', boxShadow: '0 10px 24px rgba(0,0,0,.2)' }}>
                {initials || 'U'}
              </Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-.02em' }}>{user.userName || 'User'}</Typography>
                  <Chip label="My Profile" size="small" sx={{ bgcolor: 'rgba(255,255,255,.16)', color: '#fff', fontWeight: 800, border: '1px solid rgba(255,255,255,.24)' }} />
                </Stack>
                <Typography sx={{ mt: 0.5, color: 'rgba(255,255,255,.78)' }}>{user.userEmail || 'No email available'}</Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 5 }, bgcolor: '#fff' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3.5} alignItems="stretch">
              <Box sx={{ flex: '0 0 36%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 2, bgcolor: '#eaf1ff', color: '#2454a6' }}><IoPersonOutline size={20} /></Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#173971' }}>Account Information</Typography>
                </Stack>
                <Typography color="text.secondary" fontSize={13.5} sx={{ mb: 2.5 }}>Your account details are managed by the administrator.</Typography>
                <Stack spacing={1.5}>
                  {[
                    { label: 'Username', value: user.userName, icon: <IoPersonOutline /> },
                    { label: 'Email address', value: user.userEmail, icon: <IoMailOutline /> },
                  ].map((item) => (
                    <Box key={item.label} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f7f9fd', border: '1px solid #e4eaf3' }}>
                      <Stack direction="row" spacing={1.4} alignItems="center">
                        <Box sx={{ display: 'grid', placeItems: 'center', color: '#3767aa', fontSize: 20 }}>{item.icon}</Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>{item.label}</Typography>
                          <Typography sx={{ color: '#172b4d', fontWeight: 800, overflowWrap: 'anywhere' }}>{item.value || '-'}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#e5eaf2' }} />

              <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, p: { xs: 0, md: '2px 6px' } }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 2, bgcolor: '#e9f8f2', color: '#16805d' }}><IoShieldCheckmarkOutline size={21} /></Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: '#173971' }}>Password & Security</Typography>
                </Stack>
                <Typography color="text.secondary" fontSize={13.5} sx={{ mb: 2.5 }}>Choose a strong password that you do not use elsewhere.</Typography>
                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
                {success ? <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert> : null}
                <Stack spacing={2}>
                  <TextField label="Current Password" value={form.currentPassword} onChange={handleChange('currentPassword')} required fullWidth autoComplete="current-password" {...passwordFieldProps('currentPassword', <IoLockClosedOutline />)} />
                  <TextField label="New Password" value={form.newPassword} onChange={handleChange('newPassword')} required fullWidth autoComplete="new-password" helperText="Use at least 8 characters." {...passwordFieldProps('newPassword', <IoKeyOutline />)} />
                  <TextField label="Confirm New Password" value={form.confirmPassword} onChange={handleChange('confirmPassword')} required fullWidth autoComplete="new-password" {...passwordFieldProps('confirmPassword', <IoShieldCheckmarkOutline />)} />
                  <Button type="submit" variant="contained" disabled={saving} startIcon={<IoKeyOutline />} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minWidth: 190, py: 1.15, borderRadius: 2, fontWeight: 800, textTransform: 'none', boxShadow: '0 8px 18px rgba(36,84,166,.22)' }}>
                    {saving ? 'Updating Password...' : 'Update Password'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
